import React, { useState } from 'react';
import { Button, Input, Space, Table, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { adminApi } from '@/services/api';
import type { ExpV2HistoryEntry } from '@/types/experimentsV2';
import { apiErrorMessage } from '@/utils/apiError';
import { END_REASON_LABEL, istTime } from './display';

/** Every assignment period of one player, newest first as the server sends them. */
export const HistoryTable: React.FC<{ entries: ExpV2HistoryEntry[]; loading?: boolean }> = ({
  entries,
  loading,
}) => (
  <Table<ExpV2HistoryEntry>
    size="small"
    loading={loading}
    rowKey={(r) => `${r.experimentKey}:${r.startedAt}`}
    dataSource={entries}
    pagination={{ pageSize: 50, hideOnSinglePage: true }}
    locale={{ emptyText: 'No periods for this player' }}
    columns={[
      {
        title: 'Test',
        dataIndex: 'experimentKey',
        render: (k: string) => <Link to={`/experiments-v2/${k}`}>{k}</Link>,
      },
      { title: 'Layer', dataIndex: 'layerKey' },
      { title: 'Variant', dataIndex: 'variant' },
      { title: 'Started (IST)', dataIndex: 'startedAt', render: (v: string) => istTime(v) },
      {
        title: 'Ended (IST)',
        dataIndex: 'endedAt',
        render: (v?: string) =>
          v ? (
            istTime(v)
          ) : (
            <Tag color="green" data-open>
              open
            </Tag>
          ),
      },
      {
        title: 'Why it ended',
        dataIndex: 'endReason',
        render: (v?: ExpV2HistoryEntry['endReason']) => (v ? END_REASON_LABEL[v] : ''),
      },
      {
        title: 'Source',
        dataIndex: 'source',
        render: (v: ExpV2HistoryEntry['source']) =>
          v === 'forced' ? (
            <Tag color="purple" data-forced>
              QA forced
            </Tag>
          ) : (
            'hash'
          ),
      },
      {
        title: 'First exposed (IST)',
        dataIndex: 'firstExposedAt',
        render: (v?: string) => (v ? istTime(v) : '—'),
      },
    ]}
  />
);

export const HistoryPanel: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [entries, setEntries] = useState<ExpV2HistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async () => {
    const id = userId.trim();
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminApi.experimentsV2.playerHistory(id);
      setEntries(data);
    } catch (e) {
      setEntries(null);
      setError(apiErrorMessage(e, 'Could not load the history'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} data-section="history">
      <Space wrap>
        <Input
          id="expv2-history-user"
          style={{ width: 360 }}
          placeholder="Player id"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          onPressEnter={() => void search()}
        />
        <Button icon={<SearchOutlined />} loading={loading} onClick={() => void search()}>
          Search
        </Button>
      </Space>
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
      {entries && <HistoryTable entries={entries} loading={loading} />}
    </Space>
  );
};
